import React, { useState } from 'react';
import { Info, HelpCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

interface FAQItemProps {
  question: string;
  answer: string;
}

const FAQItem: React.FC<FAQItemProps> = ({ question, answer }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="mb-4 last:mb-0">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex justify-between items-center text-left text-lg font-bold text-navy-900 p-4 rounded-lg hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center">
          <HelpCircle className="w-5 h-5 mr-3 text-navy-700 flex-shrink-0" />
          {question}
        </div>
        {isOpen ? <ChevronUp className="w-5 h-5 text-navy-700" /> : <ChevronDown className="w-5 h-5 text-navy-700" />}
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <div className="pl-8 pr-4 pb-4 text-gray-600 leading-relaxed">
              {answer}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

function PricingSuggestions() {
  const pricingData = [
    {
      category: "Desarrollo Web",
      items: [
        { name: "React + TypeScript Templates", price: "$149 - $299", features: "Componentes reutilizables, Tailwind CSS, APIs integradas" },
        { name: "Next.js Boilerplates", price: "$199 - $399", features: "SSR, API Routes, Authentication" },
        { name: "Vue.js Templates", price: "$129 - $279", features: "Composition API, Vuex, Router" },
        { name: "HTML/CSS Templates", price: "$39 - $89", features: "Responsive, SEO optimizado" }
      ]
    },
    {
      category: "E-commerce",
      items: [
        { name: "Tienda Completa React", price: "$299 - $499", features: "Carrito, Pagos, Panel Admin" },
        { name: "WooCommerce Theme", price: "$149 - $299", features: "Personalizable, Optimizado" },
        { name: "Shopify Theme", price: "$199 - $399", features: "Páginas Premium" },
        { name: "Email Templates", price: "$99 - $199", features: "Marketing, Responsive" }
      ]
    },
    {
      category: "Diseño UI/UX",
      items: [
        { name: "Figma UI Kit", price: "$99 - $399", features: "Componentes, Variables, Auto-layout" },
        { name: "Design System", price: "$199 - $399", features: "Documentación, Tokens" },
        { name: "Wireframe Kit", price: "$49 - $99", features: "Prototipado Rápido" },
        { name: "Icon Pack", price: "$29 - $79", features: "SVG, Personalizable" }
      ]
    },
    {
      category: "Recursos Gráficos",
      items: [
        { name: "Stock Photos Pack", price: "$39 - $199", features: "Alta Resolución, Exclusivas" },
        { name: "Ilustraciones", price: "$49 - $149", features: "Vectorial, Editable" },
        { name: "3D Assets", price: "$29 - $99", features: "Modelos, Texturas" },
        { name: "Patrones y Texturas", price: "$15 - $49", features: "4K, Seamless" }
      ]
    }
  ];

  const faqs = [
    {
      question: "¿Cómo determino el precio inicial de mi producto?",
      answer: "Analiza factores clave como la complejidad técnica, tiempo de desarrollo, originalidad del diseño y el valor que aporta al cliente final. Investiga precios de productos similares en el mercado y considera tu experiencia y reputación."
    },
    {
      question: "¿Puedo ofrecer diferentes niveles de licencia?",
      answer: "Sí, es una excelente estrategia ofrecer diferentes niveles de licencia. Por ejemplo: licencia personal para uso único, licencia comercial para múltiples proyectos, y licencia extendida para redistribución."
    },
    {
      question: "¿Cómo puedo destacar mi producto en el marketplace?",
      answer: "Enfócate en crear una presentación profesional con imágenes de alta calidad, documentación detallada y demostraciones en vivo cuando sea posible. Mantén tu producto actualizado y responde rápidamente a las preguntas."
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100">
      {/* Header */}
      <header className="bg-gradient-to-r from-navy-900 to-navy-700 text-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6 leading-tight">
              Guía de Precios para Assets Digitales
            </h1>
            <p className="text-lg md:text-xl text-gray-200 leading-relaxed max-w-3xl mx-auto">
              Precios competitivos basados en análisis de mercado y tendencias del sector
            </p>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-12">
        {/* Intro Section */}
        <div className="max-w-6xl mx-auto mb-12">
          <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
            <div className="flex items-start">
              <Info className="w-6 h-6 text-navy-700 mt-1 mr-4 flex-shrink-0" />
              <p className="text-gray-700">
                Esta guía proporciona rangos de precios recomendados basados en un análisis exhaustivo del mercado. 
                Los precios pueden variar según la calidad, complejidad y características únicas de cada producto.
              </p>
            </div>
          </div>
        </div>

        {/* Pricing Table */}
        <div className="max-w-6xl mx-auto overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full bg-white rounded-lg shadow-lg">
              <thead>
                <tr className="bg-navy-900 text-white">
                  <th className="py-4 px-6 text-left">Categoría / Producto</th>
                  <th className="py-4 px-6 text-left">Rango de Precios</th>
                  <th className="py-4 px-6 text-left hidden md:table-cell">Características</th>
                </tr>
              </thead>
              <tbody>
                {pricingData.map((category, categoryIndex) => (
                  <React.Fragment key={categoryIndex}>
                    <tr className="bg-navy-700 bg-opacity-10">
                      <td colSpan={3} className="py-3 px-6 font-semibold text-navy-900">
                        {category.category}
                      </td>
                    </tr>
                    {category.items.map((item, itemIndex) => (
                      <tr 
                        key={`${categoryIndex}-${itemIndex}`}
                        className={`
                          border-b border-gray-200 hover:bg-gray-50 transition-colors
                          ${itemIndex === category.items.length - 1 ? 'border-b-2 border-gray-300' : ''}
                        `}
                      >
                        <td className="py-4 px-6 text-navy-800">{item.name}</td>
                        <td className="py-4 px-6 font-medium text-navy-700">{item.price}</td>
                        <td className="py-4 px-6 text-gray-600 hidden md:table-cell">{item.features}</td>
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* FAQ Section */}
        <section className="max-w-3xl mx-auto mt-16">
          <h2 className="text-2xl font-bold text-navy-900 mb-8 text-center">
            Preguntas Frecuentes
          </h2>
          <div className="bg-white rounded-lg shadow-lg p-6 md:p-8">
            {faqs.map((faq, index) => (
              <FAQItem key={index} {...faq} />
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}

export default PricingSuggestions;